import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import config from '../config';
import {
  Container,
  Box,
  Typography,
  Button,
  Paper,
  CircularProgress,
  Alert,
  Snackbar,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
  Divider,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from '@mui/material';
import { 
  ArrowBack as ArrowBackIcon,
  Add as AddIcon,
  List as ListIcon,
  Send as SendIcon,
  Delete as DeleteIcon,
  Edit as EditIcon
} from '@mui/icons-material';
import FormBuilder from '../components/forms/FormBuilder';

const TestFormBuilder = () => {
  const { eventId: paramEventId } = useParams();
  const navigate = useNavigate();
  const [eventId, setEventId] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadingSave, setLoadingSave] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [forms, setForms] = useState([]);
  const [selectedForm, setSelectedForm] = useState(null);
  const [isNewForm, setIsNewForm] = useState(false);
  const [formTitle, setFormTitle] = useState('New Event Form');
  const [formDescription, setFormDescription] = useState('');
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [currentForm, setCurrentForm] = useState({
    id: null,
    title: 'New Event Form',
    description: '',
    is_published: false,
    sections: [
      {
        id: `section-${Date.now()}`,
        title: 'Section 1',
        description: '',
        order: 0,
        questions: []
      }
    ]
  });

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  // Get event ID from session storage or params and fetch forms
  useEffect(() => {
    const storedEventId = sessionStorage.getItem('formBuilderEventId');
    if (storedEventId) {
      setEventId(storedEventId);
      fetchForms(storedEventId);
      // Clean up session storage
      sessionStorage.removeItem('formBuilderEventId');
    } else if (paramEventId) {
      setEventId(paramEventId);
      fetchForms(paramEventId);
    } else {
      setLoading(false);
    }
  }, [paramEventId]);

  const fetchForms = async (eventId) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('access_token');
      const response = await axios.get(
        `${config.API_BASE_URL}/api/forms/event-forms/?event_id=${eventId}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      setForms(response.data || []);
    } catch (error) {
      console.error('Error fetching forms:', error);
      setSnackbar({
        open: true,
        message: 'Failed to load forms. Please try again.',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateNewForm = () => {
    setCurrentForm({
      id: null,
      title: 'New Event Form',
      description: '',
      is_published: false,
      sections: [
        {
          id: `section-${Date.now()}`,
          title: 'Section 1',
          description: '',
          order: 0,
          questions: []
        }
      ]
    });
    setFormTitle('New Event Form');
    setFormDescription('');
    setIsNewForm(true);
    setActiveTab(1);
  };

  const handleSelectForm = (form) => {
    setSelectedForm(form);
    setCurrentForm(form);
    setFormTitle(form.title);
    setFormDescription(form.description);
    setActiveTab(1);
  };

  const handleSaveForm = async () => {
    try {
      setLoadingSave(true);
      const token = localStorage.getItem('access_token');
      const formData = {
        ...currentForm,
        title: formTitle,
        description: formDescription,
        event: eventId
      };

      let response;
      if (isNewForm) {
        response = await axios.post(
          `${config.API_BASE_URL}/api/forms/`,
          formData,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );
      } else {
        response = await axios.put(
          `${config.API_BASE_URL}/api/forms/${currentForm.id}/`,
          formData,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );
      }

      setSnackbar({
        open: true,
        message: `Form ${isNewForm ? 'created' : 'updated'} successfully!`,
        severity: 'success'
      });

      // Refresh forms list
      await fetchForms(eventId);
      setShowSaveDialog(false);
      
      // If this was a new form, update the current form with the ID
      if (isNewForm) {
        setCurrentForm(response.data);
        setIsNewForm(false);
      }
    } catch (error) {
      console.error('Error saving form:', error);
      setSnackbar({
        open: true,
        message: `Failed to ${isNewForm ? 'create' : 'update'} form. Please try again.`,
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleFormChange = (updatedForm) => {
    setCurrentForm(updatedForm);
  };

  const handleOpenSaveDialog = () => {
    setShowSaveDialog(true);
  };

  const handleBack = () => {
    const returnPath = sessionStorage.getItem('formBuilderReturnPath') || '/';
    sessionStorage.removeItem('formBuilderReturnPath');
    navigate(returnPath);
  };

  // Simulate loading form data
  useEffect(() => {
    const timer = setTimeout(() => {
      setLoading(false);
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
          sx={{ mb: 2 }}
        >
          Back to Event
        </Button>
        {eventId && (
          <Typography variant="subtitle1" color="textSecondary" sx={{ mt: 1, mb: 3 }}>
            Managing forms for Event ID: {eventId}
          </Typography>
        )}

        <Paper sx={{ mb: 4 }}>
          <Tabs
            value={activeTab}
            onChange={(e, newValue) => setActiveTab(newValue)}
            indicatorColor="primary"
            textColor="primary"
            variant="fullWidth"
          >
            <Tab label="My Forms" icon={<ListIcon />} />
            <Tab label="Form Builder" icon={<EditIcon />} />
          </Tabs>

          <Box p={3}>
            {activeTab === 0 && (
              <Box>
                <Box display="flex" justifyContent="flex-end" mb={2}>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<AddIcon />}
                    onClick={handleCreateNewForm}
                  >
                    Create New Form
                  </Button>
                </Box>

                {forms.length > 0 ? (
                  <List>
                    {forms.map((form) => (
                      <React.Fragment key={form.id}>
                        <ListItem 
                          button 
                          onClick={() => handleSelectForm(form)}
                          selected={selectedForm?.id === form.id}
                        >
                          <ListItemText
                            primary={form.title}
                            secondary={form.description || 'No description'}
                          />
                          <IconButton
                            edge="end"
                            aria-label="edit"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSelectForm(form);
                              setActiveTab(1);
                            }}
                          >
                            <EditIcon />
                          </IconButton>
                        </ListItem>
                        <Divider />
                      </React.Fragment>
                    ))}
                  </List>
                ) : (
                  <Box textAlign="center" py={4}>
                    <Typography variant="body1" color="textSecondary" gutterBottom>
                      No forms found for this event.
                    </Typography>
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={<AddIcon />}
                      onClick={handleCreateNewForm}
                      sx={{ mt: 2 }}
                    >
                      Create Your First Form
                    </Button>
                  </Box>
                )}
              </Box>
            )}
            {activeTab === 1 && (
              <Box>
                <Box mb={3}>
                  <TextField
                    fullWidth
                    label="Form Title"
                    value={formTitle}
                    onChange={(e) => setFormTitle(e.target.value)}
                    margin="normal"
                    variant="outlined"
                  />
                  <TextField
                    fullWidth
                    label="Form Description"
                    value={formDescription}
                    onChange={(e) => setFormDescription(e.target.value)}
                    margin="normal"
                    variant="outlined"
                    multiline
                    rows={2}
                  />
                </Box>

                <Paper sx={{ p: 3, mb: 3 }}>
                  <FormBuilder 
                    form={currentForm} 
                    onChange={handleFormChange} 
                  />
                </Paper>

                <Box display="flex" justifyContent="flex-end" mt={3}>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<SendIcon />}
                    onClick={handleOpenSaveDialog}
                    disabled={!formTitle.trim()}
                  >
                    {isNewForm ? 'Create Form' : 'Update Form'}
                  </Button>
                </Box>
              </Box>
            )}
          </Box>
        </Paper>

        {/* Save Confirmation Dialog */}
        <Dialog
          open={showSaveDialog}
          onClose={() => setShowSaveDialog(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            {isNewForm ? 'Create New Form' : 'Update Form'}
          </DialogTitle>
          <DialogContent>
            <Typography>
              {isNewForm 
                ? 'Are you sure you want to create this form?' 
                : 'Are you sure you want to update this form?'}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button 
              onClick={() => setShowSaveDialog(false)}
              color="primary"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveForm}
              color="primary"
              variant="contained"
              disabled={loadingSave}
              startIcon={loadingSave ? <CircularProgress size={20} /> : null}
            >
              {isNewForm ? 'Create' : 'Update'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert 
            onClose={() => setSnackbar({ ...snackbar, open: false })} 
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </Container>
  );
};

export default TestFormBuilder;
